import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { Stop } from 'modelos/src';
import { HelperService } from '../../../../../../../../auxiliares/servicios/helper.service';
import { ItemCategoria } from '../lista-categoria/lista-categoria.component';

type StopConReason = Stop & {
  reason?: string;
};

type EstadoMotorParada = 'apagado' | 'encendido';

/**
 * Paradas del viaje. Mapea las paradas de tripero a la categoría genérica y
 * filtra por estado del motor; el armazón y el estilo son de app-lista-categoria.
 *
 * Presentacional: no busca ni conoce el estado del mapa, sólo emite la parada
 * elegida.
 */
@Component({
  selector: 'app-lista-paradas',
  templateUrl: './lista-paradas.component.html',
  styleUrl: './lista-paradas.component.scss',
  standalone: false,
})
export class ListaParadasComponent {
  helper = inject(HelperService);
  // El formato de fecha se resuelve en el mapeo y no en el template: la
  // categoría genérica recibe textos ya armados.
  private fecha = new DatePipe('es-AR');

  @Input() set paradas(value: Stop[] | undefined) {
    this._paradas = value ?? [];
    this.actualizarItems();
  }
  get paradas(): Stop[] {
    return this._paradas;
  }
  private _paradas: Stop[] = [];

  /** Parada resaltada (la seleccionada en el mapa). */
  @Input() set seleccionada(value: Stop | undefined) {
    this._seleccionada = value;
    this.actualizarItems();
  }
  get seleccionada(): Stop | undefined {
    return this._seleccionada;
  }
  private _seleccionada?: Stop;

  /**
   * Estado del motor por el que filtrar. Se puede manejar desde afuera (el
   * formulario de la búsqueda) o con el select propio de la lista.
   */
  @Input() set estadoMotor(value: '' | EstadoMotorParada) {
    this._estadoMotor = value ?? '';
    this.actualizarItems();
  }
  get estadoMotor(): '' | EstadoMotorParada {
    return this._estadoMotor;
  }
  private _estadoMotor: '' | EstadoMotorParada = '';

  /** El select propio sobra cuando el filtro ya está en el formulario. */
  @Input() mostrarFiltroMotor = true;

  /** Minutos detenido a partir de los que se contó, para mostrarlo. */
  @Input() umbralMinutos?: number | null;

  /** Las paradas se están calculando. */
  @Input() cargando = false;

  @Output() seleccionar = new EventEmitter<Stop>();

  public items: ItemCategoria<Stop>[] = [];

  public onEstadoMotorChange(): void {
    this.actualizarItems();
  }

  public get umbral(): string | undefined {
    return this.umbralMinutos ? `≥${this.umbralMinutos} min` : undefined;
  }

  public get textoVacio(): string {
    return this.estadoMotor
      ? 'Sin paradas para el estado de motor elegido'
      : 'Sin paradas detectadas';
  }

  // El Output de la categoría no es genérico (los templates no propagan el
  // tipo), así que el dato vuelve como unknown y se castea acá.
  public verEnMapa(item: ItemCategoria): void {
    const parada = item.dato as Stop | undefined;
    if (parada) this.seleccionar.emit(parada);
  }

  /**
   * Estado del motor durante la parada, deducido del motivo con el que tripero
   * la detectó (`determineStopReason`). Son sólo dos: el motor estaba andando
   * o no.
   *
   * - `ignition_off`: la ignición estaba apagada.
   * - `no_movement` / `parking`: la ignición estaba encendida. Tripero sólo
   *   llega a estos dos motivos después de descartar la ignición apagada, así
   *   que ambos implican motor andando (ralentí, semáforo, espera).
   * - `gap`: el tracker dejó de reportar y la parada se dedujo del hueco entre
   *   posiciones, así que no hay dato de ignición. Va con los apagados: en
   *   estas instalaciones el tracker se queda sin energía cuando el vehículo se
   *   apaga (es lo que detecta el powerDiagnostic de tripero), así que el hueco
   *   es la firma habitual de un vehículo apagado.
   */
  private estadoMotorDeParada(reason?: string): EstadoMotorParada {
    return reason === 'no_movement' || reason === 'parking'
      ? 'encendido'
      : 'apagado';
  }

  private actualizarItems(): void {
    this.items = this._paradas
      .map((parada) => ({
        parada,
        estado: this.estadoMotorDeParada((parada as StopConReason).reason),
      }))
      .filter(({ estado }) => !this.estadoMotor || this.estadoMotor === estado)
      .map(({ parada, estado }) => ({
        id: `${parada.startTime}`,
        titulo:
          this.fecha.transform(parada.startTime, 'dd/MM/yyyy HH:mm') ?? '',
        // El estado del motor va por parada y no como subtítulo de un grupo:
        // así se lee en la misma línea que la hora, sin partir la lista en dos
        descripcion:
          estado === 'apagado' ? 'Detenido y Apagado' : 'Detenido y Encendido',
        detalles: [
          {
            label: 'Duración',
            valor: this.helper.parseSeconds(parada.duration),
            icono: 'schedule',
          },
          ...(parada.endTime
            ? [
                {
                  label: 'Hora fin',
                  valor: this.fecha.transform(parada.endTime, 'HH:mm') ?? '',
                  icono: 'schedule',
                },
              ]
            : []),
        ],
        seleccionado: this.seleccionada === parada,
        dato: parada,
      }));
  }
}
